// Client-side mirror of the backend deliverable lifecycle state machine.
// Source of truth: backend/src/models/tracking.ts LIFECYCLE_TRANSITIONS +
// canTransition. Any change to the backend table must be reflected here.
// This module is intentionally a leaf (no component imports) so it can be
// imported by both UI components and tests without triggering vi.mock coupling.

import type { DeliverableLifecycleEvent, LifecycleEventType } from "../../../../types";

// Mirrors backend LIFECYCLE_TRANSITIONS in backend/src/models/tracking.ts.
// Keep this table in sync with the backend source — the backend is canonical.
export const LIFECYCLE_TRANSITIONS: Record<
  LifecycleEventType | "not-started",
  readonly LifecycleEventType[]
> = {
  "not-started": ["submitted"],
  submitted: [
    "under-review",
    "revision-requested",
    "conditionally-accepted",
    "accepted",
    "rejected",
    "failed-acceptance",
  ],
  "under-review": [
    "revision-requested",
    "conditionally-accepted",
    "accepted",
    "rejected",
    "failed-acceptance",
  ],
  "revision-requested": ["submitted"],
  "conditionally-accepted": [
    "accepted",
    "under-review",
    "awaiting-resubmission",
    "failed-acceptance",
    "rejected",
  ],
  "awaiting-resubmission": ["submitted"],
  "failed-acceptance": ["awaiting-resubmission"],
  accepted: ["closed", "reverted"],
  rejected: ["reverted"],
  closed: ["reverted"],
  reverted: [],
};

// Mirrors backend canTransition in backend/src/models/tracking.ts.
export function canTransition(
  from: LifecycleEventType | "not-started",
  to: LifecycleEventType,
): boolean {
  return (LIFECYCLE_TRANSITIONS[from] as readonly string[]).includes(to);
}

// Walk the event array applying canTransition + chronological-consistency
// in one pass. Returns { ok: true } on success, or { ok: false, error }
// with a user-facing message on the first violation found.
// An empty array is valid (clears the log — server permits this).
export function validateLifecycleChain(
  events: Pick<DeliverableLifecycleEvent, "type" | "date">[],
): { ok: boolean; error?: string } {
  if (events.length === 0) return { ok: true };

  let previousState: LifecycleEventType | "not-started" = "not-started";
  let previousDate: string | null = null;

  for (let i = 0; i < events.length; i++) {
    const event = events[i];

    // Chronological consistency: each event date must be >= the previous.
    if (previousDate !== null && event.date < previousDate) {
      return {
        ok: false,
        error: `Event ${i + 1} (${event.type}) date ${event.date} is before the previous event date ${previousDate}.`,
      };
    }

    // State-machine guard: the transition from previousState → event.type must be legal.
    if (!canTransition(previousState, event.type)) {
      return {
        ok: false,
        error: `Cannot transition from "${previousState}" to "${event.type}".`,
      };
    }

    previousState = event.type;
    previousDate = event.date;
  }

  return { ok: true };
}
